
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const parseTimeoutEnv = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const AUTH_CRITICAL_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_API_CRITICAL_TIMEOUT_MS, 60000);
const LOGIN_REQUEST_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_LOGIN_TIMEOUT_MS, Math.max(120000, AUTH_CRITICAL_TIMEOUT_MS));
const VERIFY_REQUEST_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_AUTH_VERIFY_TIMEOUT_MS, AUTH_CRITICAL_TIMEOUT_MS);


// Always attach token from localStorage for every request
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete config.headers['Authorization'];
    }
    return config;
  },
  error => Promise.reject(error)
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const lastVerifyRef = React.useRef(0);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    lastVerifyRef.current = 0;
    delete api.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await api.get('/auth/verify', {
          timeout: VERIFY_REQUEST_TIMEOUT_MS
        });
        setUser(response.data.data.user);
        lastVerifyRef.current = Date.now();
      } catch (error) {
        console.error('Token verification failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Only verify if we haven't verified in the last 5 minutes
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      if (now - lastVerifyRef.current > fiveMinutes) {
        verifyToken();
      } else {
        // Reuse cached user state, just mark as loaded
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

const login = async (identifier, password) => {
  try {
    const response = await api.post('/auth/login', { identifier, password }, {
      timeout: LOGIN_REQUEST_TIMEOUT_MS
    });
    // Try different possible response structures
    let newToken, userData;
    
    if (response.data.data) {
      // Expected structure: { success: true, data: { access_token: '...', user: {...} } }
      newToken = response.data.data.access_token;
      userData = response.data.data.user;
    } else if (response.data.access_token) {
      // Alternative structure: { access_token: '...', user: {...} }
      newToken = response.data.access_token;
      userData = response.data.user;
    } else if (response.data.token) {
      // Another alternative: { token: '...', user: {...} }
      newToken = response.data.token;
      userData = response.data.user;
    }
    
    
    if (!newToken) {
      throw new Error('No token received from server');
    }
    
    // Set token first, then user - this ensures isAuthenticated becomes true immediately
    localStorage.setItem('token', newToken);
    
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    setToken(newToken);
    setUser(userData);

    try {
      const verifyRes = await api.get('/auth/verify', {
        timeout: VERIFY_REQUEST_TIMEOUT_MS
      });
      if (verifyRes?.data?.data?.user) {
        setUser(verifyRes.data.data.user);
      }
      lastVerifyRef.current = Date.now();
    } catch (_) {
      // Keep login response user if verify call fails.
    }

    setLoading(false);
    return { success: true };
  } catch (error) {
    setLoading(false);
    console.error('[AuthContext] login() error response:', error.response);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Login failed'
    };
  }
};  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!token && !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
